import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/http';
import { ApiError } from '@/features/auth/api';

export type UserRole = 'USER' | 'ADMIN';

export interface AdminUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface UpdateUserInput {
  firstName: string;
  lastName: string;
  role: UserRole;
}

export const ADMIN_USERS_QUERY_KEY = ['admin', 'users'] as const;

export function useUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: () => request<AdminUser[]>('/api/admin/users'),
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, CreateUserInput>({
    mutationFn: (input) =>
      request<AdminUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: number; input: UpdateUserInput }>({
    mutationFn: ({ id, input }) =>
      request<void>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => request<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY }),
  });
}

export function useForceSyncUser() {
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => request<void>(`/api/admin/users/${id}/sync`, { method: 'POST' }),
  });
}

export function useForceSyncAllUsers() {
  return useMutation<void, ApiError, void>({
    mutationFn: () => request<void>('/api/admin/users/sync', { method: 'POST' }),
  });
}
